<template>
    <div class="portal-card-wrapper portal-card-wrapper--large">
        <div class="portal-card confirmation-card">
            <div class="confirmation-header">
                <div class="success-icon-badge">
                    <common-icon name="check" size="ultra-large" class="header-check-icon"></common-icon>
                </div>
                <h2 class="confirm-main-title">
                    {{ $t('pages.public.record_upload.step_confirm.title') }}
                </h2>
                <p class="confirm-main-subtitle">
                    {{ $t('pages.public.record_upload.step_confirm.subtitle') }}
                </p>
            </div>

            <div class="portal-card__body confirm-body">
                <div class="meta-grid-2x2">
                    <div class="meta-tile">
                        <p class="meta-tile__label">
                            {{ $t('pages.public.record_upload.step_confirm.request_id') }}
                        </p>
                        <p class="meta-tile__value font-mono">{{ requestId }}</p>
                    </div>
                    <div class="meta-tile">
                        <p class="meta-tile__label">
                            {{ $t('pages.public.record_upload.step_confirm.files_uploaded') }}
                        </p>
                        <p class="meta-tile__value">{{ filesCountText }}</p>
                    </div>
                    <div class="meta-tile">
                        <p class="meta-tile__label">
                            {{ $t('pages.public.record_upload.step_confirm.sent_to') }}
                        </p>
                        <p class="meta-tile__value font-semibold">{{ sentTo }}</p>
                    </div>
                    <div class="meta-tile">
                        <p class="meta-tile__label">
                            {{ $t('pages.public.record_upload.step_confirm.submitted_at') }}
                        </p>
                        <p class="meta-tile__value font-semibold">{{ submittedAt }}</p>
                    </div>
                </div>

                <div v-if="deliveredFiles && deliveredFiles.length > 0" class="files-delivered-section">
                    <p class="files-delivered-label">
                        {{ $t('pages.public.record_upload.step_confirm.files_delivered') }}
                    </p>
                    <div class="files-delivered-box">
                        <div v-for="(file, index) in deliveredFiles" :key="index" class="delivered-file-row">
                            <div class="delivered-file-left">
                                <common-icon name="check" size="extra-small" class="green-check-icon"></common-icon>
                                <span class="delivered-file-name">{{ file.name }}</span>
                            </div>
                            <span class="delivered-file-cat">{{ getCategoryLabel(file.category) }}</span>
                        </div>
                    </div>
                </div>

                <div class="info-notice-box">
                    <common-icon name="emergancy" size="extra-small" class="info-notice-icon"></common-icon>
                    <div class="info-notice-text">
                        {{ $t('pages.public.record_upload.step_confirm.receipt_notice') }}
                    </div>
                </div>

                <div class="return-btn-wrapper">
                    <button type="button" class="btn-return-dashboard" @click="handleReturn">
                        <common-icon name="left-arrow" size="small"></common-icon>
                        <span>{{ $t('pages.public.record_upload.step_confirm.return_dashboard') }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'step-confirm',
        props: {
            requestId: { type: String, default: 'REQ001' },
            sentTo: { type: String, default: 'Downtown Medical Associates' },
            submittedAt: { type: String, default: '9/15/2026, 5:22:44 PM' },
            deliveredFiles: {
                type: Array,
                default: () => [],
            },
        },
        computed: {
            filesCountText() {
                const count = this.deliveredFiles.length
                return this.$tc('pages.public.record_upload.step_confirm.files_count', count, {
                    count,
                })
            },
        },
        methods: {
            handleReturn() {
                this.$emit('return')
            },
            getCategoryLabel(category) {
                if (!category) return this.$t('pages.public.record_upload.categories.other')
                const categoryKeyMap = {
                    'Complete Medical History': 'complete_medical_history',
                    'Prescription History': 'prescription_history',
                    'Lab Results': 'lab_results',
                    'Surgical Records': 'surgical_records',
                    'Imaging Reports': 'imaging_reports',
                    'Immunization Records': 'immunization_records',
                    'Consultation Notes': 'consultation_notes',
                    'Mental Health Records': 'mental_health_records',
                    Other: 'other',
                }
                const key = categoryKeyMap[category]
                if (key) {
                    return this.$t(`pages.public.record_upload.categories.${key}`)
                }
                return category
            },
        },
    }
</script>

<style scoped lang="scss">
    $brand-success: #16a34a;
    $brand-border: #bbf7d0;

    .portal-card-wrapper {
        width: 100%;
    }

    .confirmation-card {
        background: #ffffff;
        border-radius: 16px;
        border: 1px solid $brand-border;
        box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05);
        overflow: hidden;
    }

    .confirmation-header {
        background: #16a34a;
        padding: 32px 32px 28px;
        color: #ffffff;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .success-icon-badge {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.2);
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
    }

    .header-check-icon {
        color: #ffffff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .confirm-main-title {
        font-size: 24px;
        font-weight: 700;
        margin: 0 0 8px;
        color: #ffffff;
    }

    .confirm-main-subtitle {
        font-size: 14px;
        color: #dcfce7;
        margin: 0;
        line-height: 1.45;
    }

    .confirm-body {
        padding: 28px 32px;
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    /* 2x2 Metadata Grid */
    .meta-grid-2x2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
    }

    .meta-tile {
        background: #f9fafb;
        border-radius: 12px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 4px;

        &__label {
            font-size: 12px;
            color: #9ca3af;
            margin: 0;
        }

        &__value {
            font-size: 14px;
            font-weight: 700;
            color: #111827;
            margin: 0;

            &.font-mono {
                font-family: monospace;
            }

            &.font-semibold {
                font-weight: 600;
            }
        }
    }

    /* Files Delivered */
    .files-delivered-section {
        border: 1px solid #f1f5f9;
        border-radius: 12px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .files-delivered-label {
        font-size: 12px;
        font-weight: 600;
        color: #9ca3af;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        margin: 0 0 4px 0;
    }

    .files-delivered-box {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .delivered-file-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        font-size: 14px;
    }

    .delivered-file-left {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }

    .green-check-icon {
        color: #16a34a;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
    }

    .delivered-file-name {
        font-size: 14px;
        color: #374151;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .delivered-file-cat {
        font-size: 12px;
        color: #9ca3af;
        margin-left: auto;
        flex-shrink: 0;
    }

    /* Info Notice Box */
    .info-notice-box {
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        border-radius: 8px;
        padding: 12px 16px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    .info-notice-icon {
        color: #2563eb;
        flex-shrink: 0;
        margin-top: 2px;
        display: inline-flex;
        align-items: center;
    }

    .info-notice-text {
        font-size: 14px;
        line-height: 1.5;
        color: #1e40af;
    }

    /* Return Button */
    .return-btn-wrapper {
        margin-top: 4px;
    }

    .btn-return-dashboard {
        width: 100%;
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 10px 18px;
        font-size: 14px;
        font-weight: 500;
        color: #0f172a;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: background-color 0.15s ease;

        &:hover {
            background-color: #f8fafc;
        }
    }

    @media (max-width: 640px) {
        .meta-grid-2x2 {
            grid-template-columns: 1fr;
        }
        .confirmation-header {
            padding: 24px 20px;
        }
        .confirm-body {
            padding: 20px;
        }
    }
</style>
